'use client';

import { GameScoreInput } from '@/components/atoms';
import { GAME_NUMBER_TO_INDEX, GAME_NUMBERS } from '@/components/constants/constants';
import { MatchSide, RetireOption, ScoreFormatConfig } from '@/components/constants/types';
import { useScoreCard } from '@/contexts/ScoreCardProvider';
import { MatchupStatusEnum } from '@/entities/matchup/constants';
import { MatchScores } from '@/lib/types';
import { getVisibleGames } from '@/utils/helpers';

interface ScoreInputContainerProps {
	isReadOnly?: boolean;
	scoreFormat: number;
	matchId: string;
	team: MatchSide;
	showDropdown?: boolean;
	retiredGameNumber?: string;
	retireTeam?: string;
	retiredInGame?: string;
	retireOption?: string;
	matchWinner?: 'home' | 'away' | 'draw';
	matchupStatus?: MatchupStatusEnum;
	value?: number;
	homeRetiredGameNumber?: string;
	awayRetiredGameNumber?: string;
	games?: ScoreFormatConfig['games'];
	retiredGames?: string[];
	retiredTeams?: Record<string, string>;
	retireOptions?: Record<string, RetireOption>;
	adjustedScores?: Record<string, { homeScore: number; awayScore: number }>;
	lockedGames?: Record<string, boolean>;
}

// TODO-tala: create teamScoreKey similar to BE response
type ScoreKey = `game${number}` | 'final';
const createScoreKey = (gameNumber: string, index: number): ScoreKey => (gameNumber === GAME_NUMBERS.FINAL ? 'final' : `game${index + 1}`);

export const ScoreInputContainer = ({
	isReadOnly = false,
	scoreFormat,
	matchId,
	team,
	showDropdown = true,
	// retiredGameNumber,
	// retireTeam,
	// retiredInGame,
	// retireOption,
	matchWinner,
	matchupStatus,
	// value,
	// homeRetiredGameNumber,
	// awayRetiredGameNumber,
	games,
	retiredGames = [],
	retiredTeams = {},
	retireOptions = {},
	adjustedScores = {},
	lockedGames = {}
}: ScoreInputContainerProps) => {
	const { getMatchScores, updateMatchScores, setTempMatchRetireScores, tempMatchRetireScores } = useScoreCard();
	const scores = getMatchScores(matchId)[team];

	const fullScores: MatchScores = getMatchScores(matchId);
	const opponentTeam = team === 'home' ? 'away' : 'home';
	const opponentScore = fullScores[opponentTeam][`game${GAME_NUMBERS}`] ?? '0';

	const getGameScore = (gameNumber: string) => {
		const index = GAME_NUMBER_TO_INDEX[gameNumber];
		if (index === undefined) return 0;

		const scoreKey = createScoreKey(gameNumber, index);
		return Number(scores[scoreKey as keyof typeof scores]) || 0;
	};

	const handleScoreUpdate = (gameNumber: string, value: number) => {
		const index = GAME_NUMBER_TO_INDEX[gameNumber];
		if (index === undefined) return;

		const scoreKey = createScoreKey(gameNumber, index);
		const update = { [scoreKey]: value.toString() };

		// If this is a retired game for this team, update the temp scores
		if (retiredGames.includes(gameNumber) && retiredTeams[gameNumber] === team) {
			if (team === 'home') {
				setTempMatchRetireScores({
					...tempMatchRetireScores,
					homeRetiredGameValue: value.toString() || adjustedScores[gameNumber]?.homeScore?.toString()
				});
			} else {
				setTempMatchRetireScores({
					...tempMatchRetireScores,
					awayRetiredGameValue: value.toString() || adjustedScores[gameNumber]?.awayScore?.toString()
				});
			}
		}
		updateMatchScores(matchId, team, update);
	};

	const renderGameScoreInput = (gameNumber: string) => {
		const isGameLocked = isReadOnly || gameNumber === GAME_NUMBERS.FINAL || lockedGames[gameNumber];

		const isRetired = retiredGames.includes(gameNumber);
		const isRetiredTeam = isRetired && retiredTeams[gameNumber] === team;
		const retireOption = isRetired ? retireOptions[gameNumber] : '';

		const isMatchWinner = gameNumber === GAME_NUMBERS.FINAL && matchWinner === team;

		let initialValue = getGameScore(gameNumber);

		if (isRetired && adjustedScores[gameNumber]) {
			initialValue = team === 'home' ? adjustedScores[gameNumber].homeScore : adjustedScores[gameNumber].awayScore;
		}

		return (
			<GameScoreInput
				key={gameNumber}
				gameNumber={gameNumber}
				initialValue={initialValue}
				initialRetireStatus={isRetiredTeam ? retireOption : ''}
				onChange={(value: number) => handleScoreUpdate(gameNumber, value)}
				isLocked={isGameLocked}
				scoreFormat={scoreFormat}
				showDropdown={showDropdown}
				matchId={matchId}
				team={team}
				matchWinner={isMatchWinner}
				matchupStatus={matchupStatus}
				games={games}
				opponentScore={Number(opponentScore)}
			/>
		);
	};

	return (
		<div className="flex items-start justify-center gap-[10px]">
			{getVisibleGames(scoreFormat).map(({ gameNumber }) => renderGameScoreInput(gameNumber))}
		</div>
	);
};
